import { createSyncOperation } from "../../../domain/entities";
import { createAuditRecord } from "../../audit/recorders/createAuditRecord";
import { TABLES } from "../db";
import { runLocalTransaction } from "../db/runLocalTransaction";

const getEntityRecordId = (entity) => entity.id ?? entity.vesselId;

export class AtomicOperationalWriteCoordinator {
  constructor({
    db,
    auditRepository,
    syncOperationRepository,
  }) {
    this.db = db;
    this.auditRepository = auditRepository;
    this.syncOperationRepository = syncOperationRepository;
  }

  async commitEntityWrite({
    entity,
    entityType,
    entityRepository,
    operation,
    actorId,
    summary,
    payload,
    scope,
  }) {
    const [{ auditEntry, syncOperation }] = await this.commitBatch({
      actorId,
      operations: [
        {
          mode: "put",
          entity,
          entityType,
          entityRepository,
          operation,
          summary,
          payload,
          scope,
        },
      ],
    });

    return {
      entity,
      auditEntry,
      syncOperation,
    };
  }

  async commitBatch({ actorId, operations }) {
    const auditEntries = [];
    const syncOperations = [];
    const touchedTables = new Set([TABLES.AUDIT_ENTRIES, TABLES.SYNC_OPERATIONS]);

    operations.forEach(({ entityRepository }) => {
      touchedTables.add(entityRepository.table.name);
    });

    await runLocalTransaction(
      this.db,
      async () => {
        for (const operation of operations) {
          const {
            mode = "put",
            entity,
            entityId: explicitEntityId,
            entityType,
            entityRepository,
            operation: auditOperation,
            summary,
            payload,
            scope,
            entityRevision,
          } = operation;

          const entityId = explicitEntityId ?? getEntityRecordId(entity);
          const auditEntry = createAuditRecord({
            entityType,
            entityId,
            operation: auditOperation,
            actorId,
            revision: entityRevision ?? entity?.revision,
            summary,
            payload,
          });

          const syncOperation = createSyncOperation({
            organizationId: scope.organizationId,
            vesselId: scope.vesselId,
            voyageId: scope.voyageId,
            tripId: scope.tripId,
            entityType,
            entityId,
            entityRevision: entityRevision ?? entity?.revision ?? 1,
            operation: mode === "delete" ? "delete" : "upsert",
            status: "pending",
          });

          if (mode === "delete") {
            await entityRepository.delete(entityId);
          } else {
            await entityRepository.put(entity);
          }

          await this.auditRepository.put(auditEntry);
          await this.syncOperationRepository.put(syncOperation);

          auditEntries.push(auditEntry);
          syncOperations.push(syncOperation);
        }
      },
      Array.from(touchedTables)
    );

    return operations.map((operation, index) => ({
      ...operation,
      auditEntry: auditEntries[index],
      syncOperation: syncOperations[index],
    }));
  }
}
